// @ts-strict-ignore
import { Features } from '@backend/lib-feature-mgr';
import { useCallback, useMemo, useState } from 'react';

import { Select } from '~/components/Select';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useSearchFormContext } from '~/contexts/SearchFormContext/SearchFormContext';
import {
  DEFAULT_CATEGORY_TYPE,
  IN_COUNTRY_CATEGORY_TYPES,
  useCheckFeatureEnabled,
  useGetAddressDetails,
  usePredictions,
  useTranslation,
} from '~/hooks';

type AddressSelectProps = {
  fontSize?: 'l' | 'm';
};

export const AddressSelect = ({ fontSize }: AddressSelectProps) => {
  const t = useTranslation();
  const { iframe } = useBusinessUnitContext();
  const [search, setSearch] = useState<string | null>(null);
  const locationMandatoryFeature = useCheckFeatureEnabled(Features.JobBoardLocationMandatory);
  const inCountrySearchCategoriesFeature = useCheckFeatureEnabled(Features.JobBoardInCountrySearchCategories);

  const isRequired = useMemo(() => !iframe && locationMandatoryFeature, [iframe, locationMandatoryFeature]);

  const {
    form: {
      address: { value, onChange, onValidityChange },
    },
  } = useSearchFormContext();

  const getDetails = useGetAddressDetails();
  const predictionTypes = useMemo(
    () => (inCountrySearchCategoriesFeature ? IN_COUNTRY_CATEGORY_TYPES : [DEFAULT_CATEGORY_TYPE]),
    [inCountrySearchCategoriesFeature],
  );
  const [predictions, , getPredictions] = usePredictions({
    types: predictionTypes,
  });

  const handleChange = useCallback(
    async (newValue: string | null) => {
      if (!newValue) {
        setSearch(null);
        onChange(null);
        return;
      }

      const id = predictions?.find(({ address }) => address === newValue)?.id;
      if (!id) return;

      const addressDetails = await getDetails(id);
      onChange(addressDetails);
    },
    [getPredictions, getDetails, onChange, predictions],
  );

  const handleSearchChange = useCallback(
    async (newSearch: string | null) => {
      setSearch(newSearch);
      if (!newSearch) return;
      await getPredictions(newSearch);
    },
    [getPredictions],
  );

  const predictionsOptions = useMemo(
    () => predictions?.map(({ address }) => ({ value: address })) || [],
    [predictions],
  );

  return (
    <Select
      clearable={!isRequired}
      placeholder={t('form_placeholders.address')}
      required={isRequired}
      search={search}
      fontSize={fontSize}
      onChange={handleChange}
      value={value?.name || ''}
      options={predictionsOptions}
      onSearchChange={handleSearchChange}
      disableEnterSelection
      onValidityChange={onValidityChange}
    />
  );
};
